import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuthStore } from "../state/authStore"
import { useDeviceStore } from "../state/deviceStore"
import { useOrderStore } from "../state/orderStore"
import { logout } from "../api/auth"

const MAX_NARUDZBI = 10

export default function NarudzbeListPage() {
    const navigate = useNavigate()
    const token = useAuthStore(s => s.token)
    const ime = useAuthStore(s => s.ime)
    const prezime = useAuthStore(s => s.prezime)
    const companyName = useAuthStore(s => s.companyName)
    const deviceNaziv = useDeviceStore(s => s.deviceNaziv)
    const clearAuth = useAuthStore(s => s.clear)

    const narudzbe = useOrderStore(s => s.narudzbe)
    const kreirajNarudzbu = useOrderStore(s => s.kreirajNarudzbu)
    const obrisiNarudzbu = useOrderStore(s => s.obrisiNarudzbu)
    const setAktivna = useOrderStore(s => s.setAktivna)
    const ocistiSve = useOrderStore(s => s.ocistiSve)

    const [showNovaModal, setShowNovaModal] = useState(false)
    const [novaStol, setNovaStol] = useState("")

    const handleLogout = async () => {
        if (narudzbe.length > 0) {
            const ukupnoStavki = narudzbe.reduce((s, n) => s + n.items.length, 0)
            const poruka = `Imate ${narudzbe.length} ${narudzbe.length === 1 ? "aktivnu narudžbu" : "aktivne narudžbe"} sa ukupno ${ukupnoStavki} ${ukupnoStavki === 1 ? "stavkom" : "stavki"}. Sve će biti obrisano. Sigurno želite da se odjavite?`
            if (!window.confirm(poruka)) return
        }
        if (token) await logout(token).catch(() => { })
        clearAuth()
        ocistiSve()
        navigate("/login", { replace: true })
    }

    const handleKreirajNovu = () => {
        if (narudzbe.length >= MAX_NARUDZBI) {
            alert(`Maksimalan broj aktivnih narudžbi je ${MAX_NARUDZBI}. Završi jednu prije nego dodaš novu.`)
            return
        }
        setNovaStol("")
        setShowNovaModal(true)
    }

    const potvrdiNovu = () => {
        const id = kreirajNarudzbu(novaStol || null)
        setShowNovaModal(false)
        navigate(`/order/${id}`)
    }

    const handleOtvori = (id: string) => {
        setAktivna(id)
        navigate(`/order/${id}`)
    }

    const handleObrisi = (id: string, e: React.MouseEvent) => {
        e.stopPropagation()
        if (window.confirm("Obrisati ovu narudžbu?")) {
            obrisiNarudzbu(id)
        }
    }

    const sortedNarudzbe = [...narudzbe].sort((a, b) => b.izmjenjeno - a.izmjenjeno)

    return (
        <div className="flex-1 flex flex-col h-full overflow-hidden">
            <header
                className="px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between text-slate-800 shadow-soft flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #B9D1EA 0%, #D7E4F2 100%)" }}
            >
                <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm sm:text-lg truncate">{ime} {prezime}</div>
                    <div className="text-xs text-slate-600 truncate">{companyName} · {deviceNaziv}</div>
                </div>
                <div className="flex gap-1 sm:gap-2 flex-shrink-0 ml-2">
                    <button
                        onClick={() => navigate("/active")}
                        className="bg-white/70 hover:bg-white active:bg-white/90 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold border border-capActive shadow-soft"
                    >
                        Aktivne
                    </button>
                    <button
                        onClick={handleLogout}
                        className="bg-white/70 hover:bg-white active:bg-white/90 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold border border-capActive shadow-soft"
                    >
                        Odjava
                    </button>
                </div>
            </header>

            <div className="flex-1 overflow-y-auto bg-antique-light p-3 sm:p-4">
                <div className="max-w-3xl mx-auto">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg sm:text-xl font-bold text-slate-800">
                            Moje narudžbe {narudzbe.length > 0 && <span className="text-slate-500 text-sm font-normal">({narudzbe.length}/{MAX_NARUDZBI})</span>}
                        </h2>
                        <button
                            onClick={handleKreirajNovu}
                            className="bg-primary-600 hover:bg-primary-700 active:bg-primary-900 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-medium flex items-center gap-1"
                        >
                            <span className="text-lg leading-none">+</span> Nova narudžba
                        </button>
                    </div>

                    {narudzbe.length === 0 ? (
                        <div className="bg-white rounded-xl shadow-soft p-8 text-center border border-antique">
                            <div className="text-5xl mb-3 opacity-40">📋</div>
                            <div className="text-slate-600 font-medium mb-2">Nemate aktivnih narudžbi</div>
                            <div className="text-sm text-slate-500">Tap na "Nova narudžba" da kreneš</div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {sortedNarudzbe.map(n => {
                                const ukupno = n.items.reduce((s, i) => s + i.cijena * i.kolicina, 0)
                                return (
                                    <button
                                        key={n.id}
                                        onClick={() => handleOtvori(n.id)}
                                        className="w-full bg-white border border-capActive rounded-xl p-3 sm:p-4 text-left hover:border-primary-500 hover:shadow-medium active:scale-[0.98] shadow-soft transition-all"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-bold text-slate-800">Narudžba #{n.broj}</span>
                                                    {n.stol && (
                                                        <span className="bg-primary-100 text-primary-800 px-2 py-0.5 rounded text-xs font-semibold">
                                                            Stol: {n.stol}
                                                        </span>
                                                    )}
                                                </div>
                                                {n.items.length > 0 ? (
                                                    <div className="text-sm text-slate-600 mt-1 truncate">
                                                        {n.items.slice(0, 3).map(i => i.nazivArtikla).join(", ")}
                                                        {n.items.length > 3 && ` +${n.items.length - 3} još`}
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-slate-400 italic mt-1">Prazna narudžba</div>
                                                )}
                                                <div className="text-xs text-slate-500 mt-1">
                                                    {n.items.length} {n.items.length === 1 ? "stavka" : "stavki"} · {ukupno.toFixed(2)} KM
                                                </div>
                                            </div>
                                            <button
                                                onClick={(e) => handleObrisi(n.id, e)}
                                                className="bg-red-50 hover:bg-red-100 active:bg-red-200 text-red-600 w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0"
                                                aria-label="Obriši narudžbu"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>

            {showNovaModal && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
                        <h3 className="text-lg font-bold text-slate-800 mb-2">Nova narudžba</h3>
                        <p className="text-sm text-slate-500 mb-4">Opciono - upiši naziv stola ili gosta</p>
                        <input
                            type="text"
                            value={novaStol}
                            onChange={e => setNovaStol(e.target.value)}
                            placeholder="npr. Stol 5, Bar 2, Terasa"
                            className="w-full border-2 border-slate-300 rounded-lg px-3 py-3 text-base focus:border-primary-500 focus:outline-none"
                            autoFocus
                            onKeyDown={e => {
                                if (e.key === "Enter") potvrdiNovu()
                                if (e.key === "Escape") setShowNovaModal(false)
                            }}
                        />
                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={() => setShowNovaModal(false)}
                                className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 py-3 rounded-lg font-medium"
                            >
                                Odustani
                            </button>
                            <button
                                onClick={potvrdiNovu}
                                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold"
                            >
                                Kreiraj
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}